import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'

export default function Password({ adminId }) {
    const history = useHistory()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [newPassword, setNewPassword] = useState('')
    const [newPassword2, setNewPassword2] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')

    useEffect(() => {
        const getData = async () => {
            if (!adminId) {
                const errorMessage = '필수 파라미터가 없어 관리자 정보를 불러올 수 없습니다.'
                setHasDetail(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDetailErrorMessage('')

            try {
                const response = await API.post('/api/v1/admin/admin/getAdmin', { adminId: adminId })

                if (response.data.code === 1) {
                    const adminData = response.data.data || {}
                    setHasDetail(Boolean(adminData?.adminId))
                    setDetailErrorMessage(adminData?.adminId ? '' : '관리자 정보를 찾을 수 없습니다.')
                } else if (response.data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = response.data.message || '관리자 정보를 불러오지 못했습니다.'
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '관리자 정보를 불러오지 못했습니다.'
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [adminId, history])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 관리자 조회 실패 시 다른 상세 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="관리자 정보를 불러오지 못했습니다."
            description={detailErrorMessage || '홈으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/" visible={false} backLabel="홈으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    const updateData = async () => {
        try {
            setSaving(true)

            const response = await API.post('/api/v1/admin/admin/updatePassword', { adminId: adminId, password: newPassword, password2: newPassword2 })

            if (response.data.code === 1) {
                history.push({ pathname: `/`, openSnackbar: true, severitySnackbar: 'success', messageSnackbar: response.data.message })
            } else if (response.data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('비밀번호를 변경하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (saving) {
            return
        }
        if (newPassword.length < 6) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('비밀번호는 6자리 이상입니다.')
            return
        }
        if (newPassword !== newPassword2) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('비밀번호가 다릅니다.')
            return
        }
        updateData()
    }
    return (
        <Grid item xs={12}>
            {loading ? (
                    <Grid item xs={12}>
                        <Spiner/>
                        {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                    </Grid>
                ) : !hasDetail ? (
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                        <Title>비밀번호 수정</Title>
                        {renderDetailErrorState()}
                        {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                    </Paper>
                ) : (
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                        <Title>비밀번호 수정</Title>
                        <Box component="form" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                            <fieldset disabled={saving} style={{ border: 0, margin: 0, padding: 0 }}>
                                <Grid container spacing={2} sx={{ marginTop: 2 }}>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                required
                                                label="새로운 비밀번호"
                                                type="password"
                                                name="password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                required
                                                label="새로운 비밀번호 확인"
                                                type="password"
                                                name="password2"
                                                value={newPassword2}
                                                onChange={(e) => setNewPassword2(e.target.value)}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </fieldset>
                            <BottomButtonGroup path="/" visible={true} backLabel="홈으로" submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} />
                        </Box>
                        {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                    </Paper>
                )}
        </Grid>
    )
}
